import React, { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { doc, updateDoc, Timestamp } from "firebase/firestore";
import * as XLSX from "xlsx";
import db from "./Firebase";

const compactPhone = (val) => String(val || "").replace(/\D/g, "").slice(-10);

const throttledUpdates = async (phones, endTs, onProgress) => {
    let updated = 0, notFound = 0;
    const CONCURRENCY = 50;
    for (let i = 0; i < phones.length; i += CONCURRENCY) {
        const chunk = phones.slice(i, i + CONCURRENCY);
        const results = await Promise.allSettled(
            chunk.map(phone => updateDoc(doc(db, "funder_users_list", phone), { end_date: endTs }))
        );
        results.forEach(r => {
            if (r.status === "fulfilled") updated++;
            else notFound++;
        });
        onProgress(updated + notFound, phones.length);
    }
    return { updated, notFound };
};

export default function UPlusRevoke() {
    const [phones, setPhones] = useState([]);
    const [endDate, setEndDate] = useState("");
    const [fileName, setFileName] = useState("");
    const [status, setStatus] = useState(null);
    const [progress, setProgress] = useState({ done: 0, total: 0 });
    const [result, setResult] = useState(null);
    const fileRef = useRef();

    const handleFile = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setFileName(file.name);
        setResult(null);
        setStatus(null);
        const reader = new FileReader();
        reader.onload = (evt) => {
            const wb = XLSX.read(evt.target.result, { type: "binary" });
            const ws = wb.Sheets[wb.SheetNames[0]];
            const data = XLSX.utils.sheet_to_json(ws);
            const parsed = data
                .map(row => compactPhone(row.phone || row.Phone || row.phone_no || row.Phone_no || Object.values(row)[0]))
                .filter(p => p && p.length === 10);
            const unique = [...new Set(parsed)];
            setPhones(unique);
        };
        reader.readAsBinaryString(file);
    };

    const handleSubmit = async () => {
        if (!phones.length || !endDate) return;
        setStatus("loading");
        setResult(null);
        setProgress({ done: 0, total: phones.length });
        try {
            const endTs = Timestamp.fromDate(new Date(endDate + "T23:59:59+05:30"));
            const { updated, notFound } = await throttledUpdates(phones, endTs, (done, total) => {
                setProgress({ done, total });
            });
            setResult({ updated, notFound });
            setStatus("done");
        } catch (err) {
            console.error("UPlusRevoke error:", err);
            setStatus("error");
        }
    };

    const canSubmit = phones.length > 0 && endDate && status !== "loading";

    return (
        <div className="container-fluid py-4" style={{ maxWidth: "760px" }}>

            {/* HEADER */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h3 className="mb-1">U+ End Date Updater</h3>
                    <div className="text-muted" style={{ fontSize: "13px" }}>
                        Upload Excel → select end date → update <code>funder_users_list</code>
                    </div>
                </div>
                <Link to="/" className="btn btn-outline-secondary btn-sm">Back</Link>
            </div>

            {/* STEP 1 */}
            <div className="card mb-3 border-0 shadow-sm">
                <div className="card-body">
                    <div className="fw-bold mb-1" style={{ fontSize: "14px" }}>Step 1 — Upload Excel</div>
                    <div className="text-muted mb-2" style={{ fontSize: "12px" }}>
                        Excel must have a column named <strong>phone</strong> with 10-digit numbers
                    </div>
                    <input
                        ref={fileRef}
                        type="file"
                        accept=".xlsx,.xls,.csv"
                        className="form-control"
                        onChange={handleFile}
                        style={{ maxWidth: "360px" }}
                    />
                    {phones.length > 0 && (
                        <div className="mt-2 d-flex align-items-center gap-2">
                            <span className="badge bg-primary" style={{ fontSize: "13px", padding: "6px 12px" }}>
                                {phones.length} phone numbers loaded
                            </span>
                            <span className="text-muted" style={{ fontSize: "12px" }}>{fileName}</span>
                        </div>
                    )}
                </div>
            </div>

            {/* STEP 2 */}
            <div className="card mb-3 border-0 shadow-sm">
                <div className="card-body">
                    <div className="fw-bold mb-1" style={{ fontSize: "14px" }}>Step 2 — Select New End Date</div>
                    <input
                        type="date"
                        className="form-control"
                        value={endDate}
                        onChange={e => setEndDate(e.target.value)}
                        style={{ maxWidth: "200px" }}
                    />
                </div>
            </div>

            {/* PHONE PREVIEW */}
            {phones.length > 0 && (
                <div className="card mb-3 border-0 shadow-sm">
                    <div className="card-header bg-white border-bottom" style={{ fontSize: "13px" }}>
                        <span className="fw-bold">Phone Preview</span>
                        <span className="text-muted ms-2">({phones.length} unique numbers)</span>
                    </div>
                    <div style={{ maxHeight: "180px", overflowY: "auto", padding: "12px 16px" }}>
                        <div className="d-flex flex-wrap gap-2">
                            {phones.map(p => (
                                <span key={p} className="badge bg-light text-dark border"
                                    style={{ fontSize: "12px", fontFamily: "monospace" }}>
                                    {p}
                                </span>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* SUBMIT */}
            <button
                className="btn btn-danger px-4"
                style={{ fontWeight: 700, minWidth: "180px" }}
                disabled={!canSubmit}
                onClick={handleSubmit}
            >
                {status === "loading" ? (
                    <><span className="spinner-border spinner-border-sm me-2" />
                        Updating… {progress.done}/{progress.total}</>
                ) : "Update End Dates"}
            </button>

            {/* PROGRESS BAR */}
            {status === "loading" && progress.total > 0 && (
                <div className="mt-3" style={{ maxWidth: "400px" }}>
                    <div className="progress" style={{ height: "8px" }}>
                        <div
                            className="progress-bar bg-danger"
                            style={{ width: `${Math.round((progress.done / progress.total) * 100)}%`, transition: "width 0.2s" }}
                        />
                    </div>
                    <div className="text-muted mt-1" style={{ fontSize: "12px" }}>
                        {Math.round((progress.done / progress.total) * 100)}% complete
                    </div>
                </div>
            )}

            {/* RESULT */}
            {status === "done" && result && (
                <div className="alert alert-success mt-3 d-flex gap-4 align-items-center">
                    <div>
                        <div style={{ fontSize: "1.8rem", fontWeight: 800, color: "#16a34a" }}>{result.updated}</div>
                        <div style={{ fontSize: "12px" }}>Records Updated</div>
                    </div>
                    {result.notFound > 0 && (
                        <div>
                            <div style={{ fontSize: "1.8rem", fontWeight: 800, color: "#dc2626" }}>{result.notFound}</div>
                            <div style={{ fontSize: "12px" }}>Not Found in funder_users_list</div>
                        </div>
                    )}
                </div>
            )}

            {status === "error" && (
                <div className="alert alert-danger mt-3">
                    Something went wrong. Check the browser console for details.
                </div>
            )}
        </div>
    );
}
